<!-- SetupComponentsのテンプレートを書いておくのでこれ使って新規コンポーネント作成してくれると嬉しいです(改善等あれば随時変更してください) -->
<script lang="ts" setup>
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import Modal from '@/components/modal/Modal.vue';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import TextButton from '@/components/input/Btn/TextButton.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import { orderStatusDescriptions } from '@/corePagesModule/BaseTableContent/components/order/orderStatus';
import { isPartialDelivery } from '@/coreDomainsModule/entities/partialDelivery';
import { PartialDeliveryService } from '@/orderTableModule/domains/service/partialDeliveryService';
import { isOrder } from '@/coreDomainsModule/entities/order';
import { OrderService } from '@/orderTableModule/domains/service/orderService';
import { showInfoModal } from './InfoModal/InfoModalManager';

// props
const props = withDefaults(
  defineProps<{
    items?: any[];
    modalState?: boolean;
    headerTitle?: string;
    description?: string;
    isPartialDelivery?: boolean; // 分納情報かどうか
    isBulkEdit?: boolean; // 一括編集情報かどうか
  }>(),
  {
    items: undefined,
    modalState: false,
    headerTitle: undefined,
    description: undefined,
    isPartialDelivery: false,
    isBulkEdit: false,
  }
);

// emits
const emits = defineEmits<{
  (e: 'canceled'): void;
  (e: 'confirm', orderStatus: OrderStatus): void;
}>();

//変数
const t = i18n.t;
const selectedOrderStatus = ref<OrderStatus>();

watch(
  () => props.modalState,
  (newVal, oldVal) => {
    // false -> true になった場合、変数を初期化する
    if (oldVal === false && newVal === true) {
      selectedOrderStatus.value = undefined;
    }
  }
);

/**
 * 無効にするステータスかどうかを管理するためのcomputed
 */
const disabledStatusMap = computed(() => {
  const map = new Map<OrderStatus, boolean>();
  for (const status of orderStatusDescriptions) {
    map.set(status.status, isDisabledStatus(status.status));
  }
  return map;
});

/**
 * 無効にするステータスかどうか
 * @param status - ステータス
 */
const isDisabledStatus = (status: OrderStatus) => {
  if (props.items === undefined || props.items.length === 0)
    // ステータスが推奨か承認待ちの場合は無効
    return status === OrderStatus.Recommended || status === OrderStatus.ApprovalRequested;
  if (props.isPartialDelivery && props.items.some((item) => isPartialDelivery(props.items)))
    return props.items.some((item) => !PartialDeliveryService.canTransitionToStatus(item, status));
  if (props.items.some((item) => isOrder(item))) {
    // 一括編集の場合は、分納のステータスを無効にする
    if (props.isBulkEdit && status === OrderStatus.PartialDelivery) return true;
    return props.items.some((item) => !OrderService.canTransitionOrderStatus(item, status));
  }
  return false;
};

/**
 * ステータス適用
 */
const applyOrderStatus = async () => {
  // 分納から承認済みにする場合は、確認モーダルを表示
  if (
    props.items?.some((item) => isOrder(item)) &&
    props.items?.some((item) => item.orderStatus === OrderStatus.PartialDelivery) &&
    selectedOrderStatus.value === OrderStatus.ApprovedAwaitingDeliveryDate
  ) {
    const result = await showInfoModal({
      modalTitle: t('確認'),
      modalDescription: t('分納情報が削除されますが、よろしいですか？'),
      colorStyle: 'primary',
      nextButtonLabel: t('はい'),
      backButtonLabel: t('いいえ'),
    });
    if (!result) return;
  }

  emits('confirm', selectedOrderStatus.value as OrderStatus);
};
</script>

<template>
  <Modal
    class="confirm-modal-container"
    :modal-size="'max-content'"
    :stretch-container="true"
    :scroll-off="true"
    :padding="'16px'"
    :modal-state="modalState"
    :border-radius="'16px'"
    @outside-click="emits('canceled')"
  >
    <div class="revise-content">
      <div class="revise-content__header header">
        <span class="header__title">
          {{ t(headerTitle ?? 'ステータスの一括修正') }}
        </span>
        <span class="header__description">
          {{ t(description ?? 'チェックをつけた発注のステータスを一括修正することができます') }}
        </span>
      </div>

      <div class="revise-content__order-status order-status">
        <template v-for="(status, index) in orderStatusDescriptions" :key="index">
          <div
            class="order-status__container"
            :class="{ 'order-status__container--disabled': disabledStatusMap.get(status.status) }"
            @click="!disabledStatusMap.get(status.status) ? (selectedOrderStatus = status.status) : null"
          >
            <OrderStatusBadge
              :class="[selectedOrderStatus === status.status ? 'blur' : '']"
              class="order-status__container--badge"
              :order-status-str="status.status"
            />
            <span :class="[selectedOrderStatus === status.status ? 'blur' : '']" class="order-status__container--description">{{
              t(status.description)
            }}</span>
            <div v-if="selectedOrderStatus === status.status" class="order-status__container--hover checked-container">
              <img class="checked-container__image" src="@/assets/img/status-check-icon.png" />
              <span class="checked-container__text">{{ t('選択する') }}</span>
            </div>
          </div>
        </template>
      </div>

      <div class="revise-content__button-container button-container">
        <TextButton
          :button-style="'btn-cancel'"
          :size="'btn-md'"
          :on-click="() => emits('canceled')"
          class="button-container--cancel-button"
        >
          {{ t('キャンセル') }}
        </TextButton>
        <TextButton
          :button-style="'btn-primary'"
          :size="'btn-md'"
          :disabled="!selectedOrderStatus"
          :on-click="applyOrderStatus"
          class="button-container--confirm-button"
        >
          {{ t('適用') }}
        </TextButton>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.revise-content {
  display: grid;
  grid-template-rows: repeat(3, auto);
  &__header {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 16px;
  }
  &__order-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  }
  &__button-container {
    display: grid;
    grid-template-columns: repeat(2, fit-content(50%));
  }
}

.header {
  display: grid;
  grid-template-rows: 1fr 1fr;
  align-items: center;
  justify-items: center;
  gap: 16px;
  &__title {
    font-size: 22px;
    font-weight: bold;
    line-height: 28px;
  }
  &__description {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  }
}

.order-status {
  display: grid;
  grid-template-columns: repeat(3, 172px);
  grid-template-rows: repeat(4, minmax(120px, 1fr));
  gap: 8px;
  &__container {
    position: relative;
    height: 100%;
    display: grid;
    grid-template-rows: 1fr 1fr;
    align-items: center;
    justify-items: center;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    padding: 16px 12px 16px 12px;
    cursor: pointer;
    &--badge {
      width: 100%;
      height: 100%;
    }
    &--description {
      justify-self: start;
      font-size: 12px;
      line-height: 14px;
      font-weight: 400;
    }
    &--hover {
      height: 100%;
      width: 100%;
      position: absolute;
      background-color: rgba($color: #000000, $alpha: 0.4);
      border-radius: 4px;
      cursor: pointer;
    }
    &--disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: rgba($color: #000000, $alpha: 0.6);
    }
  }
}

.blur {
  -ms-filter: blur(3px);
  filter: blur(3px); // ぼかす
}

.checked-container {
  display: grid;
  grid-template-rows: repeat(2, fit-content(50%));
  align-items: center;
  justify-items: center;
  align-content: center;
  gap: 8px;
  &__image {
    width: 40px;
    height: 40px;
  }
  &__text {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $semanticWhite;
  }
}

.button-container {
  display: grid;
  grid-template-columns: repeat(2, fit-content(50%));
  justify-self: center;
  gap: 16px;
  &--cancel-button {
    padding: 12px 32px 12px 32px;
  }
  &--confirm-button {
    padding: 12px 56px 12px 56px;
  }
}
</style>
